import * as React from "react";
import { Notification } from "@/components/admin/notification";
import { useConfigurationContext } from "@/components/mimin/root/ConfigurationContext";
import { useSignertUrl } from "@/components/mimin/providers/commons/useSignertVedlegg";
import { cn } from "@/lib/utils";

/**
 * Rammen rundt alle innloggingsskjermene: logo, overskrift og ett kort midt på
 * en mørk flate med et svakt merkevareskjær bak.
 *
 * Hvorfor én ramme og ikke markup per side: «Glemt passord?» og «Sett nytt
 * passord» er ett klikk unna innloggingen, og skjermene ble tidligere tegnet
 * hver for seg. Da drev de fra hverandre — to ulike logoplasseringer og to
 * ulike bredder på samme reise.
 *
 * Den gamle rammen var todelt med et heldekkende teal felt til venstre. Det
 * feltet fantes bare fra `lg` og oppover, så på mobil — der folk faktisk logger
 * inn — sto skjemaet alene på en tom flate uten logo i det hele tatt.
 */
export const AuthShell = ({
  title,
  subtitle,
  children,
  footer,
  className,
}: React.PropsWithChildren<{
  title?: React.ReactNode;
  subtitle?: React.ReactNode;
  footer?: React.ReactNode;
  className?: string;
}>) => {
  const { darkModeLogo, title: appTitle } = useConfigurationContext();
  // En logo lastet opp til den private vedleggsbøtta kan ikke signeres før
  // innlogging — da skjules den heller enn å vises brutt. Medfølgende logoer
  // (/logos/…) går uendret gjennom og vises alltid.
  const logoSrc = useSignertUrl(darkModeLogo);

  return (
    <div className="relative flex min-h-screen flex-col items-center justify-center overflow-hidden bg-background px-5 py-12">
      {/* Flat bakgrunn: Mimin bruker ingen gradienter (AK45). */}

      <div className={cn("relative w-full max-w-100", className)}>
        <div className="flex flex-col items-center text-center">
          {logoSrc && (
            <img
              src={logoSrc}
              alt={appTitle}
              className="size-14 rounded-2xl shadow-lg ring-1 ring-white/10"
            />
          )}
          {title ? (
            <h1 className="mt-6 text-2xl font-semibold tracking-tight text-balance">
              {title}
            </h1>
          ) : null}
          {subtitle ? (
            <p className="mt-2 text-sm text-muted-foreground text-balance">
              {subtitle}
            </p>
          ) : null}
        </div>

        {/* Kortet: i mørkt tema har --card samme valør som bakgrunnen, så
            flaten skilles med kant og en tynn lysstripe på toppen — ikke med
            en lysere gråtone, jf. nøytralpaletten i index.css. */}
        <div
          className="mt-8 rounded-2xl border bg-card p-6 sm:p-8"
          style={{
            boxShadow:
              "inset 0 1px 0 0 rgb(255 255 255 / 0.04), 0 24px 48px -32px rgb(0 0 0 / 0.75)",
          }}
        >
          {children}
        </div>

        {footer ? (
          <div className="mt-6 text-center text-sm">{footer}</div>
        ) : null}
      </div>

      <Notification />
    </div>
  );
};
